import React from "react";
import { useRequest } from "@/components/pages/requests/hooks/useRequests";
import { useUpdateTask } from "@/components/pages/requests/hooks/tasks.hooks";
import { RequestStatusSelect } from "@/components/pages/requests/components/RequestStatusSelect";
import AssignStaffModal from "@/components/pages/requests/components/modals/AssignStaffModal";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getStatusLabel } from "@/constants/requestConstants";
import { RequestStatus } from "@/types/request";
import { getTaskAssignment } from "@/components/pages/requests/utils/taskAssignment";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

interface RequestMessageDisplayProps {
  requestId: string;
}

export const RequestMessageDisplay: React.FC<RequestMessageDisplayProps> = ({
  requestId,
}) => {
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();
  const { data: request, isLoading } = useRequest(
    requestId,
    capabilities.canUseRequestsSystem,
  );
  const { mutate: updateTask } = useUpdateTask();

  if (!capabilities.canUseRequestsSystem) {
    return null;
  }

  if (isLoading || !request) {
    return (
      <div className="animate-pulse rounded-lg bg-slate-200 p-2 text-xs">
        Loading request...
      </div>
    );
  }

  const task = request.tasks?.[0];
  const serviceName = request.service?.name || t("requests.form.service");
  const status = task?.status || request.status || RequestStatus.NEW_TASK;
  const {
    assignedTeamId,
    assignedTeamName,
    assignedStaffId,
    assignedStaffName,
  } = getTaskAssignment(task, request.service);

  let assignedLabel = t("requests.page.assign");

  if (assignedTeamId) {
    assignedLabel = assignedTeamName || assignedLabel;
  } else if (assignedStaffName) {
    assignedLabel = assignedStaffName;
  }

  const handleStatusChange = (_reqId: string, newStatus: string) => {
    if (task) {
      updateTask({ id: task.id, data: { status: newStatus as RequestStatus } });
    }
  };

  return (
    <div className="mt-2 flex min-w-[240px] flex-col gap-3 overflow-hidden rounded-lg border border-neutral-200 bg-white p-3 shadow-xs">
      <div className="flex items-center justify-between border-b border-neutral-200 pb-2">
        <span className="text-eathy-comfort-1 text-sm font-semibold">
          {serviceName}
        </span>
        <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
          {getStatusLabel(status)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {task && (
          <AssignStaffModal
            taskId={task.id}
            assignedStaffId={assignedStaffId || undefined}
            teamId={assignedTeamId || undefined}
            trigger={
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
              >
                <UserPlus className="h-3.5 w-3.5" />
                {assignedLabel}
              </Button>
            }
          />
        )}

        <RequestStatusSelect
          requestId={requestId}
          currentStatus={status}
          onStatusChange={handleStatusChange}
        />
      </div>
    </div>
  );
};
